import {
    adminApprovals,
    adminBackgroundChecks,
    adminBackoffice,
    adminCandidateIM,
    adminCandidates,
    adminCandidatesInterviewed,
    adminCheckIns,
    adminContractorInvoices,
    adminContractors,
    adminCustomerExpenses,
    adminCustomerInvoices,
    adminCustomers,
    adminDeposits,
    adminEquipment,
    adminHolidays,
    adminIMTemplates,
    adminInvoiceLines,
    adminOfferLetters,
    adminOnboarding,
    adminRevenue,
    adminTalentLeads,
} from '~/acl';
import type { BasicCandidate, BasicCustomer, BasicUser } from '~/api/v2';
import type { AuthUser } from '~/auth/user';
import { RemixIconNames, type IconNames } from '~/components/ui/Icon';
import { BACKGROUND_CHECKS, isEnabled } from '~/utils/flags';
import { fullName } from '~/utils/format';
import type { RecentEntry } from './recent';
import type { QuickNavItem, QuickNavPageKind } from './types';

type PageMeta = {
    path: string;
    icon: IconNames;
    section: QuickNavPageKind;
    canAccess: (user: AuthUser) => boolean;
    titleFallback: string;
};

const PAGE_META: PageMeta[] = [
    {
        path: '/home',
        icon: RemixIconNames.RiHomeLine,
        section: 'me',
        canAccess: () => true,
        titleFallback: 'Home',
    },
    {
        path: '/opportunities',
        icon: RemixIconNames.RiSuitcaseLine,
        section: 'me',
        canAccess: (user) =>
            (!user.isUsBased() || user.isRemotely()) &&
            !user.isTalentFreelancer() &&
            !user.isExContractor(),
        titleFallback: 'Job opportunities',
    },
    {
        path: '/tasks',
        icon: RemixIconNames.RiTaskLine,
        section: 'me',
        canAccess: (user) => user.isRemotely(),
        titleFallback: 'Tasks',
    },
    {
        path: '/settings',
        icon: RemixIconNames.RiSettingsLine,
        section: 'me',
        canAccess: (user) => user.isRemotely(),
        titleFallback: 'Settings',
    },
    {
        path: '/job_positions',
        icon: RemixIconNames.RiSuitcaseLine,
        section: 'remotely',
        canAccess: (user) => user.isRemotely(),
        titleFallback: 'Job Positions',
    },
    {
        path: '/admin/customers',
        icon: RemixIconNames.RiBuilding2Line,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminCustomers),
        titleFallback: 'Customers',
    },
    {
        path: '/admin/contractors',
        icon: RemixIconNames.RiUser2Line,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminContractors),
        titleFallback: 'Contractors',
    },
    {
        path: '/admin/candidates',
        icon: RemixIconNames.RiUserFollowLine,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminCandidates, adminCandidatesInterviewed),
        titleFallback: 'Candidates',
    },
    {
        path: '/admin/applicants',
        icon: RemixIconNames.RiUserSharedLine,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminCandidates, adminCandidatesInterviewed),
        titleFallback: 'Applicants',
    },
    {
        path: '/admin/talent-leads',
        icon: RemixIconNames.RiUserSearchLine,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminTalentLeads),
        titleFallback: 'Talent Leads',
    },
    {
        path: '/admin/referrals',
        icon: RemixIconNames.RiMegaphoneLine,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminCandidates),
        titleFallback: 'Referrals',
    },
    {
        path: '/admin/customers/deposits',
        icon: RemixIconNames.RiArchiveDrawerLine,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminDeposits),
        titleFallback: 'Deposits',
    },
    {
        path: '/admin/customers/expenses',
        icon: RemixIconNames.RiMoneyDollarBoxLine,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminCustomerExpenses),
        titleFallback: 'Expenses',
    },
    {
        path: '/admin/equipment',
        icon: RemixIconNames.RiMacbookLine,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminEquipment),
        titleFallback: 'Equipment',
    },
    {
        path: '/admin/contractors/onboarding',
        icon: RemixIconNames.RiShipLine,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminOnboarding),
        titleFallback: 'Onboarding',
    },
    {
        path: '/admin/contractors/invoices',
        icon: RemixIconNames.RiBillLine,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminContractorInvoices),
        titleFallback: 'Contractor invoices',
    },
    {
        path: '/admin/customers/invoices',
        icon: RemixIconNames.RiFileChartLine,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminCustomerInvoices),
        titleFallback: 'Customer invoices',
    },
    {
        path: '/admin/lines',
        icon: RemixIconNames.RiAlignJustify,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminInvoiceLines),
        titleFallback: 'Line items',
    },
    {
        path: '/admin/revenue',
        icon: RemixIconNames.RiMoneyDollarCircleLine,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminRevenue),
        titleFallback: 'Revenue',
    },
    {
        path: '/admin/offer-letters',
        icon: RemixIconNames.RiArticleLine,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminOfferLetters),
        titleFallback: 'Offer letters',
    },
    {
        path: '/admin/references',
        icon: RemixIconNames.RiChatCheckLine,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminCandidates),
        titleFallback: 'Reference Checks',
    },
    {
        path: '/admin/checkins',
        icon: RemixIconNames.RiQuestionAnswerLine,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminCheckIns),
        titleFallback: 'Check-Ins',
    },
    {
        path: '/admin/holidays',
        icon: RemixIconNames.RiCalendarEventLine,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminHolidays),
        titleFallback: 'Holidays',
    },
    {
        path: '/admin/insurance',
        icon: RemixIconNames.RiHealthBookLine,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminContractors),
        titleFallback: 'Health insurance',
    },
    {
        path: '/admin/profile-builder-calls',
        icon: RemixIconNames.RiCustomerServiceLine,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminCandidates),
        titleFallback: 'Profile Builder Calls',
    },
    {
        path: '/admin/background-checks',
        icon: RemixIconNames.RiShieldCheckLine,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminBackgroundChecks) && isEnabled(BACKGROUND_CHECKS),
        titleFallback: 'Background checks',
    },
    {
        path: '/admin/im/threads',
        icon: RemixIconNames.RiWhatsappLine,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminCandidateIM),
        titleFallback: 'WhatsApp threads',
    },
    {
        path: '/admin/im/templates',
        icon: RemixIconNames.RiLayout5Line,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminIMTemplates),
        titleFallback: 'WhatsApp Templates',
    },
    {
        path: '/admin/approvals',
        icon: RemixIconNames.RiCheckboxCircleLine,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminApprovals),
        titleFallback: 'Approvals',
    },
    {
        path: '/admin/backoffice',
        icon: RemixIconNames.RiPencilLine,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminBackoffice),
        titleFallback: 'Backoffice',
    },
    {
        path: '/job_positions/applications',
        icon: RemixIconNames.RiSuitcaseLine,
        section: 'remotely',
        canAccess: (user) => user.hasAccess(adminCandidates),
        titleFallback: 'Applications',
    },
    {
        path: '/admin/customers/applications',
        icon: RemixIconNames.RiBuilding2Line,
        section: 'admin',
        canAccess: (user) => user.hasAccess(adminCustomers),
        titleFallback: 'Sales Opportunities',
    },
];

export function getNavigablePages(user: AuthUser): QuickNavItem[] {
    return PAGE_META.filter((meta) => meta.canAccess(user)).map((meta) => ({
        id: meta.path,
        path: meta.path,
        label: meta.titleFallback,
        icon: meta.icon,
        admin: meta.section === 'admin',
    }));
}

export function pagesByPath(pages: QuickNavItem[]): Map<string, QuickNavItem> {
    return new Map(pages.map((page) => [page.path, page]));
}

export function resolvePagesByPaths(
    pages: QuickNavItem[],
    paths: readonly string[],
): QuickNavItem[] {
    const byPath = pagesByPath(pages);
    return paths
        .map((path) => byPath.get(path))
        .filter((page): page is QuickNavItem => Boolean(page));
}

export function toCustomerItem(customer: BasicCustomer): QuickNavItem {
    return {
        id: `customer-${customer.id}`,
        path: `/admin/customers/${customer.id}`,
        label: customer.name,
        picture: { url: customer.logo },
        customer,
    };
}

export function toContractorItem(contractor: BasicUser): QuickNavItem {
    return {
        id: `contractor-${contractor.id}`,
        path: `/admin/contractors/${contractor.id}`,
        label: fullName(contractor),
        user: contractor,
    };
}

export function toCandidateItem(candidate: BasicCandidate): QuickNavItem {
    return {
        id: `candidate-${candidate.id}`,
        path: `/candidates/${candidate.id}`,
        label: fullName(candidate.user),
        user: candidate.user,
    };
}

export function toApplicantItem(applicant: BasicCandidate): QuickNavItem {
    return {
        id: `applicant-${applicant.id}`,
        path: `/candidates/${applicant.id}`,
        label: fullName(applicant.user),
        user: applicant.user,
    };
}

export function resolveRecentItems(
    pages: QuickNavItem[],
    entries: RecentEntry[],
    canSeeCustomers: boolean,
): QuickNavItem[] {
    const byPath = pagesByPath(pages);
    return entries.flatMap((entry) => {
        if (typeof entry !== 'string') return canSeeCustomers ? [toCustomerItem(entry)] : [];
        const page = byPath.get(entry);
        return page ? [page] : [];
    });
}

export function findNavigablePath(pathname: string, pages: QuickNavItem[]): string | null {
    const byPath = pagesByPath(pages);
    if (byPath.has(pathname)) return pathname;

    const match = pages
        .filter((page) => pathname === page.path || pathname.startsWith(`${page.path}/`))
        .sort((a, b) => b.path.length - a.path.length)[0];

    return match?.path ?? null;
}
